import { useMemo, useState } from 'react';
import AppShell, { PageHeader } from '../../../components/layout/AppShell';
import { Card } from '../../../components/ui';
import { cn } from '../../../lib/cn';

function MasteryBar({ level }) {
  const pct = Math.round(level * 100);
  const colorClass =
    pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full', colorClass)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right tabular-nums">
        {pct}%
      </span>
    </div>
  );
}

function MasteriesTable({ masteries }) {
  const [filter, setFilter] = useState('all');

  // Bucket by the same rounded percentage MasteryBar renders (Math.round(level
  // * 100)), not the raw float — otherwise a level like 0.796 rounds to a
  // green "80%" bar while a raw `< 0.8` filter still buckets it as developing.
  const filtered = useMemo(() => {
    if (filter === 'all') return masteries;
    if (filter === 'weak') return masteries.filter((m) => Math.round(m.mastery_level * 100) < 50);
    if (filter === 'developing')
      return masteries.filter((m) => {
        const pct = Math.round(m.mastery_level * 100);
        return pct >= 50 && pct < 80;
      });
    if (filter === 'mastered') return masteries.filter((m) => Math.round(m.mastery_level * 100) >= 80);
    return masteries;
  }, [masteries, filter]);

  if (masteries.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Concept mastery</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Nothing tracked yet — keep practicing and this will fill in.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Concept mastery
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            ({filtered.length} of {masteries.length})
          </span>
        </h3>
        <div className="flex items-center gap-1 text-xs">
          {['all', 'weak', 'developing', 'mastered'].map((b) => (
            <button
              key={b}
              onClick={() => setFilter(b)}
              className={cn(
                'px-2 py-1 rounded',
                filter === b
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              )}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            <tr className="border-b border-gray-100 dark:border-gray-800">
              <th className="py-2 pr-3 font-medium">Concept</th>
              <th className="py-2 pr-3 font-medium">Mastery</th>
              <th className="py-2 pr-3 font-medium text-right">Correct</th>
              <th className="py-2 pr-3 font-medium text-right">Attempts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((m) => (
              <tr key={m.concept_id}>
                <td className="py-2 pr-3 text-gray-900 dark:text-white">{m.label}</td>
                <td className="py-2 pr-3">
                  <MasteryBar level={m.mastery_level} />
                </td>
                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {m.correct}
                </td>
                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                  {m.attempts}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function StudentGrammarShow({ student, grammar_summary, masteries }) {
  return (
    <AppShell student={student} scrollTitle="Grammar">
      <div className="max-w-4xl mx-auto space-y-6">
        <PageHeader
          title="Grammar"
          subtitle="Your grammar mastery overview"
          backLink="/student/profile"
          backLabel="Back to profile"
        />

        {grammar_summary && (
          <Card className="p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Overview</h3>
            <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400">
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.mastery_overview.total}</strong> assessed</span>
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.mastery_overview.mastered}</strong> mastered</span>
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.mastery_overview.developing}</strong> developing</span>
              <span><strong className="text-gray-900 dark:text-white">{grammar_summary.mastery_overview.weak}</strong> weak</span>
            </div>
          </Card>
        )}

        <MasteriesTable masteries={masteries || []} />
      </div>
    </AppShell>
  );
}
